/**
 * THEME TOGGLE COMPONENT
 * =====================
 * 
 * A segmented control toggle that displays both Light and Dark options
 * with a sliding highlight to indicate the current selection.
 * Matches modern UI patterns with smooth animations.
 */

import { Sun, Moon } from "lucide-react";
import { useTheme } from "./ThemeProvider";

interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className = "" }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();

  return (
    <div className={`inline-flex items-center ${className}`}>
      <div className="relative flex items-center bg-muted rounded-full p-1 border border-border h-9">
        {/* Sliding Background Highlight */}
        <div
          className={`absolute top-1 bottom-1 w-[calc(50%-2px)] bg-background border border-border rounded-full shadow-sm transition-transform duration-200 ease-in-out ${
            theme === "dark" ? "translate-x-[calc(100%+2px)]" : "translate-x-0"
          }`}
        />
        
        {/* Light Mode Button */}
        <button
          onClick={() => setTheme("light")}
          aria-label="Light mode"
          aria-pressed={theme === "light"}
          className={`relative z-10 flex items-center justify-center w-8 h-7 rounded-full transition-colors duration-200 ${
            theme === "light"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Sun className="h-4 w-4" />
        </button>
        
        {/* Dark Mode Button */}
        <button
          onClick={() => setTheme("dark")}
          aria-label="Dark mode"
          aria-pressed={theme === "dark"}
          className={`relative z-10 flex items-center justify-center w-8 h-7 rounded-full transition-colors duration-200 ${
            theme === "dark"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Moon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}